<!--
  SPDX-License-Identifier: MIT
  Copyright (c) 2026 Solvetia. MIT white-label additions to Chatwoot CE.
  Agent copilot panel: suggest a reply, summarize the conversation, and send bot documents.
-->
<script setup>
import { ref, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CopilotAPI from 'dashboard/api/copilot';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const store = useStore();
const { t } = useI18n();
const documents = useMapGetter('botDocuments/getBotDocuments');

const loading = ref(false);
const result = ref('');

onMounted(() => {
  store.dispatch('botDocuments/get');
});

const run = async kind => {
  loading.value = true;
  result.value = '';
  try {
    const { data } =
      kind === 'summarize'
        ? await CopilotAPI.summarize(props.conversationId)
        : await CopilotAPI.suggestReply(props.conversationId);
    if (data.error) {
      useAlert(t('COPILOT.NOT_CONFIGURED'));
    } else {
      result.value = data.content;
    }
  } catch (e) {
    useAlert(t('COPILOT.ERROR'));
  } finally {
    loading.value = false;
  }
};

const copy = () => {
  navigator.clipboard.writeText(result.value);
  useAlert(t('COPILOT.COPIED'));
};

const sendDoc = async doc => {
  try {
    await CopilotAPI.sendDocument(props.conversationId, doc.id);
    useAlert(t('COPILOT.DOC_SENT'));
  } catch (e) {
    useAlert(t('COPILOT.ERROR'));
  }
};
</script>

<template>
  <div class="flex flex-col gap-3 p-3">
    <div class="flex flex-wrap gap-2">
      <NextButton
        sm
        faded
        blue
        icon="i-lucide-sparkles"
        :label="t('COPILOT.SUGGEST')"
        :disabled="loading"
        @click="run('suggest')"
      />
      <NextButton
        sm
        faded
        slate
        icon="i-lucide-text"
        :label="t('COPILOT.SUMMARIZE')"
        :disabled="loading"
        @click="run('summarize')"
      />
    </div>

    <div v-if="loading" class="flex items-center gap-2 text-n-slate-11">
      <Spinner class="size-4" />
      <span class="text-sm">{{ t('COPILOT.THINKING') }}</span>
    </div>

    <div
      v-if="result"
      class="flex flex-col gap-2 p-2 rounded-lg bg-n-alpha-2 text-sm text-n-slate-12 whitespace-pre-wrap"
    >
      <span>{{ result }}</span>
      <NextButton
        xs
        ghost
        slate
        icon="i-lucide-copy"
        :label="t('COPILOT.COPY')"
        class="self-start"
        @click="copy"
      />
    </div>

    <div v-if="documents.length" class="flex flex-col gap-1 pt-2 border-t border-n-weak">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('COPILOT.DOCUMENTS')
      }}</span>
      <div
        v-for="doc in documents"
        :key="doc.id"
        class="flex items-center justify-between gap-2"
      >
        <span class="text-sm text-n-slate-12 truncate">{{ doc.name }}</span>
        <NextButton
          xs
          ghost
          blue
          icon="i-lucide-send"
          :label="t('COPILOT.SEND')"
          @click="sendDoc(doc)"
        />
      </div>
    </div>
  </div>
</template>
